// Auto-recovery helper for Next.js ChunkLoadError after deployments.
//
// When a new version is deployed, Next.js regenerates content hashes for all
// JS chunks. A tab opened before the deploy still references the old chunk
// URLs, so client-side navigations that dynamically import() the next page
// fail with a 404 and Next.js fires routeChangeError. A full window reload
// fetches fresh HTML with the new asset manifest, which resolves it.

// sessionStorage key holding the timestamp of the last auto-reload
export const CHUNK_RELOAD_KEY = "chunkLoadReloadAt";

// Don't auto-reload more than once per this window — prevents an infinite
// reload loop when a chunk is genuinely missing (e.g. a bad deploy).
const GUARD_WINDOW_MS = 30_000;

// Next.js surfaces chunk failures under several messages depending on the
// code path — match all known variants.
const CHUNK_ERROR_PATTERNS = [
  "Loading chunk", // webpack ChunkLoadError
  "Failed to fetch chunk", // Next.js route loader
  "Failed to load chunk", // observed in production ($exception data)
];

export function isChunkLoadError(err) {
  if (err?.name === "ChunkLoadError") return true;
  const message = err?.message ?? "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Decide whether a routeChangeError should trigger a full page reload, and if
 * so, do it (respecting the reload-loop guard).
 *
 * @param {Error} err - The error Next.js passed to routeChangeError.
 * @param {() => void} reload - Called to perform the actual reload (injectable
 *   for tests; pass `() => window.location.reload()`).
 * @returns {boolean} true when a reload was performed.
 */
export function recoverFromChunkError(err, reload) {
  // Navigation was superseded (user clicked elsewhere mid-load) — not a chunk
  // failure. Next.js marks these with `cancelled`.
  if (err?.cancelled) return false;
  if (!isChunkLoadError(err)) return false;

  // Reload-loop guard: only auto-reload once per GUARD_WINDOW_MS. If the
  // chunk is still missing after a reload, don't keep bouncing the user.
  let lastReload = 0;
  try {
    lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  } catch {
    // sessionStorage unavailable (private mode) — proceed without the guard
  }
  if (Date.now() - lastReload < GUARD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore — recovery still proceeds
  }
  reload();
  return true;
}
